import { useEffect } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { Elements } from '@stripe/react-stripe-js';
import { stripePromise } from './lib/stripe';
import App from './App';
import { AuthProvider } from './contexts/AuthContext';
import { AgeVerificationProvider } from './contexts/AgeVerificationContext';
import { CartProvider } from './contexts/CartContext';
import './index.css';
import './i18n';
import { registerServiceWorker } from './utils/notificationHelper';
import { initAnalytics } from './lib/analytics';

// Before render so early errors and the first pageview are captured.
initAnalytics();

// Create a ProfileProvider component that ensures the profile hook is available
// throughout the application
import React, { createContext, useContext } from 'react';
import { useProfile } from './hooks/useProfile';

const ProfileContext = createContext<ReturnType<typeof useProfile> | undefined>(undefined);

function ProfileProvider({ children }: { children: React.ReactNode }) {
  const profileData = useProfile();
  
  return (
    <ProfileContext.Provider value={profileData}>
      {children}
    </ProfileContext.Provider>
  );
}

export function useProfileContext() {
  const context = useContext(ProfileContext);
  if (context === undefined) {
    throw new Error('useProfileContext must be used within a ProfileProvider');
  }
  return context;
}

// Register service worker for notifications
if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    registerServiceWorker().catch(err => {
      console.error('Service worker registration failed:', err);
    });
  });
}

// Production version doesn't use StrictMode
createRoot(document.getElementById('root')!).render(
  <BrowserRouter>
    <AuthProvider>
      <ProfileProvider>
        <AgeVerificationProvider>
          <CartProvider>
            <Elements stripe={stripePromise}>
              <App />
              <Toaster position="top-right" />
            </Elements>
          </CartProvider>
        </AgeVerificationProvider>
      </ProfileProvider>
    </AuthProvider>
  </BrowserRouter>
);